import {
  Step,
  StepConnector,
  StepConnectorProps,
  StepIconProps,
  StepLabel,
  Stepper,
  stepConnectorClasses,
  stepLabelClasses,
  styled,
  useTheme,
} from '@mui/material';
import { RiCheckboxCircleFill, RiCircleLine } from '@remixicon/react';
import Color from 'color';
import theme from 'src/lib/theme';
import convertToTitleCase from 'src/utils/convertToTitleCase';
import {
  BillStatus,
  BuySellOrderStatus,
  GrowerProductStatus,
  LoadStatus,
  ProductionRunStatus,
  PurchaseOrderStatus,
  SalesOrderStatus,
} from 'types/graphql';

export const getSalesOrderStatuses = ({
  isEdi,
  isCustomerOrder,
  isExternal,
}: {
  isEdi: boolean;
  isCustomerOrder: boolean;
  isExternal: boolean;
}): SalesOrderStatus[] => {
  if (isExternal) {
    return ['DRAFT', 'SUBMITTED', 'SHIPPED', 'CLOSED'];
  }

  if (isEdi || isCustomerOrder) {
    return ['PENDING', 'APPROVED', 'PICKING', 'SHIPPED', 'CLOSED'];
  }

  return ['DRAFT', 'SUBMITTED', 'PICKING', 'SHIPPED', 'CLOSED'];
};

export const getPurchaseOrderStatuses = (soldThroughSalesOrder: boolean): PurchaseOrderStatus[] => {
  if (soldThroughSalesOrder) {
    return ['DRAFT', 'SUBMITTED', 'SHIPPED', 'CLOSED'];
  }

  return ['DRAFT', 'SUBMITTED', 'RECEIVING', 'PENDING_LIQUIDATION', 'CLOSED'];
};

export const salesOrderStatuses: SalesOrderStatus[] = [
  'DRAFT',
  'APPROVED',
  'SUBMITTED',
  'PICKING',
  'SHIPPED',
  'CLOSED',
];

export const purchaseOrderStatuses: PurchaseOrderStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'RECEIVING',
  'PENDING_LIQUIDATION',
  'SHIPPED',
  'CLOSED',
];

export const customerPortalPurchaseOrderStatuses: Array<SalesOrderStatus> = [
  'DRAFT',
  'PENDING',
  'APPROVED',
  'PICKING',
  'SHIPPED',
  'CLOSED',
];

export const buySellOrderStatuses: BuySellOrderStatus[] = ['DRAFT', 'SUBMITTED', 'PAYMENT_RECONCILIATION', 'CLOSED'];

export const financialEntityStatuses: BillStatus[] = ['DRAFT', 'OPEN', 'PAID_PARTIAL', 'PAID_FULL', 'FINALIZED'];

export const workOrderStatuses: GrowerProductStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'RECEIVING',
  'PENDING_SETTLEMENT',
  'CLOSED',
];

export const productionRunStatuses: ProductionRunStatus[] = ['DRAFT', 'IN_PROGRESS', 'COMPLETE'];

export const loadStatuses: LoadStatus[] = ['DRAFT', 'BOOKED', 'EN_ROUTE', 'COMPLETE'];

export default function StatusStepper<T extends string>({
  allStatuses,
  currentStatus,
  stageTestIdPrefix,
}: {
  allStatuses: T[];
  currentStatus: T;
  /** When provided, each stage gets a `data-testid` of `${prefix}-${status}`. */
  stageTestIdPrefix?: string;
}) {
  const currentStatusIndex = allStatuses.indexOf(currentStatus);

  return (
    <Stepper
      activeStep={currentStatusIndex}
      connector={<StatusStepConnector />}
      sx={{ alignItems: 'flex-start', mb: 4.5, px: { xs: 0.5, md: 1 } }}
    >
      {allStatuses.map((label, index) => {
        const beforeLast = index < allStatuses.length - 1;
        const isCurrent = index === currentStatusIndex;
        const stageTestId = stageTestIdPrefix ? `${stageTestIdPrefix}-${label}` : undefined;

        return (
          <Step
            key={label}
            active={isCurrent && beforeLast}
            completed={index <= currentStatusIndex}
            data-testid={stageTestId}
          >
            <StatusStepLabel slots={{ stepIcon: StatusStepIcon }}>{convertToTitleCase(label)}</StatusStepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
}

const StatusStepLabel = styled(StepLabel)(({ theme }) => ({
  [`&.${stepLabelClasses.root}`]: { flexDirection: 'column', alignItems: 'center' },
  [`& .${stepLabelClasses.iconContainer}`]: { padding: 0 },
  [`& .${stepLabelClasses.labelContainer}`]: { position: 'absolute', width: 'auto', transform: 'translateY(110%)' },
  [`& .${stepLabelClasses.label}`]: {
    [`&.${stepLabelClasses.active}`]: {
      color: Color(theme.palette.primary.main).darken(0.1).toString(),
      fontWeight: 600,
    },
    [`&.${stepLabelClasses.completed}`]: { color: Color(theme.palette.primary.light).darken(0.1).toString() },
    [`&.${stepLabelClasses.label}`]: { marginTop: 2 },
    color: Color(theme.palette.primary.light).darken(0.1).toString(),
  },
}));

const StatusStepConnector = (props: StepConnectorProps) => {
  const theme = useTheme();

  return (
    <StepConnector
      sx={{
        [`&.${stepConnectorClasses.active}`]: {
          [`&.${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
        },
        [`&.${stepConnectorClasses.completed}`]: {
          [`& .${stepConnectorClasses.line}`]: { borderColor: theme.palette.primary.main },
        },
        [`& .${stepConnectorClasses.line}`]: {
          borderColor: Color(theme.palette.primary.light).alpha(0.26).toString(),
          borderTopWidth: 3,
          borderRadius: 1,
          position: 'relative',
          top: 0, // MUI centres the connector on the icon line; any offset double-counts it.
          left: '-8px', // Adjust this value to expand the line to left
          right: '-8px', // Adjust this value to expand the line to right
          width: 'calc(100% + 16px)',
        },
      }}
      {...props}
    />
  );
};

function StatusStepIcon(props: StepIconProps) {
  const { active, completed } = props;

  if (active) {
    return <RiCircleLine size={24} style={{ color: Color(theme.palette.primary.main).darken(0.1).toString() }} />;
  }
  if (completed) {
    return <RiCheckboxCircleFill size={24} style={{ color: theme.palette.primary.main }} />;
  }

  return (
    <RiCircleLine
      size={24}
      style={{ color: Color(theme.palette.primary.light).alpha(0.5).toString(), position: 'relative' }}
    />
  );
}
